import React from 'react';
import { useFormik } from 'formik';
import { CustomInput, FormGroup, Input, Label } from 'reactstrap';
import { cloneDeep, isEqual } from 'lodash';
import { Button } from '../../../UI/Common';
import { useTranslation } from 'react-i18next';

//prettier-ignore
const __BASE = 'white', __HOVER = '#78909c', __ACTIVE = '#455a64';
const Widget4Properties = ({
  properties,
  onChange,
  monitoringPoints,
  ...props
}) => {
  const { t } = useTranslation('app');
  const { values, setFieldValue, handleChange, ...formik } = useFormik({
    initialValues: properties,
    onSubmit: () => onChange(values),
    enableReinitialize: true,
  });
  React.useEffect(() => {
    formik.handleSubmit();
  }, [values]);
  const [selected, updateSelected] = React.useState(null);
  function changeNumberValue(e, key) {
    let val = parseInt(e.target.value);
    if (Number.isNaN(val)) {
      setFieldValue(key, 1);
    } else {
      setFieldValue(key, val);
    }
  }
  function onAddAlarm(alarm) {
    const _alarms = cloneDeep(values.extraProperties.alarms);
    _alarms.push(alarm);
    setFieldValue('extraProperties.alarms', _alarms);
  }
  function onChangeOrder(direction) {
    if (!selected) {
      return;
    }
    const _alarms = cloneDeep(values.extraProperties.alarms);
    const alarmIndex = _alarms
      .map((al) => al.sensorId)
      .indexOf(selected.sensorId);
    if (direction === 'up') {
      if (!alarmIndex) {
        return;
      } else {
        [_alarms[alarmIndex], _alarms[alarmIndex - 1]] = [
          _alarms[alarmIndex - 1],
          _alarms[alarmIndex],
        ];
        setFieldValue('extraProperties.alarms', _alarms);
      }
    } else {
      if (alarmIndex === _alarms.length - 1) {
        return;
      } else {
        [_alarms[alarmIndex], _alarms[alarmIndex + 1]] = [
          _alarms[alarmIndex + 1],
          _alarms[alarmIndex],
        ];
        setFieldValue('extraProperties.alarms', _alarms);
      }
    }
  }

  function onRemoveAlarm() {
    if (!selected) {
      return;
    }
    const _alarms = cloneDeep(values.extraProperties.alarms).filter(
      (_al) => _al.sensorId !== selected.sensorId,
    );
    setFieldValue('extraProperties.alarms', _alarms);
  }

  function clickToRemoveAlarm(alarm) {
    const _alarms = cloneDeep(values.extraProperties.alarms).filter(
      (_al) => _al.sensorId !== alarm.sensorId,
    );
    setFieldValue('extraProperties.alarms', _alarms);
  }

  return (
    <div>
      <div className="d-flex align-items-center">
        <span className="w-100 text-center">{t('Properties')}</span>
        <Button
          classes="d-flex align-items-center"
          style={{ padding: '0px 2px', height: 30 }}
          onClick={() => props.setIsActive('')}
        >
          <i className="md-icon mr-0">close</i>
        </Button>
      </div>
      <FormGroup>
        <Label>Title</Label>
        <Input
          value={values.title}
          onChange={(e) => setFieldValue('title', e.target.value)}
        />
      </FormGroup>
      <FormGroup>
        <Label>Description</Label>
        <Input
          value={values.description}
          onChange={(e) => setFieldValue('description', e.target.value)}
        />
      </FormGroup>
      <div className="d-flex mb-15">
        <div className="d-flex flex-1">
          <FormGroup>
            <Label>Max Width</Label>
            <Input
              value={values.width}
              onChange={({ target }) => setFieldValue('width', target.value)}
            />
          </FormGroup>
        </div>
        <div className="d-flex flex-1" style={{ width: '50%' }}>
          <FormGroup className="w-100">
            <Label
              style={{
                width: '100%',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              Number of Columns
            </Label>
            <Input
              value={values.extraProperties.numberOfColumns}
              onChange={(e) =>
                setFieldValue('extraProperties.numberOfColumns', e.target.value)
              }
            />
          </FormGroup>
        </div>
      </div>
      <div className="d-flex mb-15">
        <div className="d-flex flex-1">
          <FormGroup>
            <Label>Column Gap</Label>
            <Input
              value={values.extraProperties.columnGap}
              onChange={({ target }) =>
                setFieldValue('extraProperties.columnGap', target.value)
              }
            />
          </FormGroup>
        </div>
        <div className="d-flex flex-1">
          <FormGroup>
            <Label>Row Gap</Label>
            <Input
              value={values.extraProperties.rowGap}
              onChange={({ target }) =>
                setFieldValue('extraProperties.rowGap', target.value)
              }
            />
          </FormGroup>
        </div>
      </div>
      <strong>Spacing around</strong>
      <div className="d-flex">
        <FormGroup>
          <Label>Top</Label>
          <Input
            type="number"
            value={values.extraProperties.spacingTop}
            onChange={({ target }) =>
              setFieldValue('extraProperties.spacingTop', target.value)
            }
          />
        </FormGroup>
        <FormGroup>
          <Label>Right</Label>
          <Input
            type="number"
            value={values.extraProperties.spacingRight}
            onChange={({ target }) =>
              setFieldValue('extraProperties.spacingRight', target.value)
            }
          />
        </FormGroup>
        <FormGroup>
          <Label>Bottom</Label>
          <Input
            type="number"
            value={values.extraProperties.spacingBottom}
            onChange={({ target }) =>
              setFieldValue('extraProperties.spacingBottom', target.value)
            }
          />
        </FormGroup>
        <FormGroup>
          <Label>Left</Label>
          <Input
            type="number"
            value={values.extraProperties.spacingLeft}
            onChange={({ target }) =>
              setFieldValue('extraProperties.spacingLeft', target.value)
            }
          />
        </FormGroup>
      </div>
      <div className="mb-10">
        <span>Available Monitoring Points</span>
        <div
          style={{
            maxHeight: 150,
            overflow: 'scroll',
            border: '0.5px solid gray',
          }}
        >
          {monitoringPoints
            .filter(
              (al) =>
                !values.extraProperties.alarms
                  .map((_al) => _al.sensorId)
                  .includes(al.sensorId),
            )
            .map((alarm) => (
              <AlarmItem
                alarm={alarm}
                onClick={onAddAlarm}
                key={alarm.sensorId}
              />
            ))}
        </div>

        <div className="mt-10">
          <span>
            Using Monitoring Points {values.extraProperties.alarms.length}
          </span>
        </div>
        <div
          style={{
            maxHeight: 150,
            minHeight: 72,
            overflow: 'scroll',
            border: '0.5px solid gray',
            marginBottom: 5,
          }}
        >
          {values.extraProperties.alarms.map((alarm, index) => (
            <UsingAlarmItem
              alarm={alarm}
              selected={selected}
              onClick={(_al) => updateSelected(_al)}
              onClickRemove={clickToRemoveAlarm}
              key={alarm.sensorId}
            />
          ))}
        </div>
        <div className="d-flex justify-content-between">
          <div>
            <Button onClick={() => onChangeOrder('up')} disabled={!selected}>
              <i className="md-icon mr-0">arrow_upward</i>
            </Button>
            <Button onClick={() => onChangeOrder('down')} disabled={!selected}>
              <i className="md-icon mr-0">arrow_downward</i>
            </Button>
          </div>
          {/* <div>
            <Button onClick={onRemoveAlarm} disabled={!selected}>
              <i className="md-icon mr-0">remove</i>
            </Button>
          </div> */}
        </div>
      </div>
      <FormGroup>
        <CustomInput
          id={`widget-${values.id}`}
          type="checkbox"
          label="Terminate Row"
          checked={values.terminateRow}
          onChange={(e) => {
            setFieldValue('terminateRow', e.target.checked);
          }}
        />
      </FormGroup>
    </div>
  );
};

const AlarmItem = ({ alarm, ...props }) => {
  const [bgColor, setBgColor] = React.useState('white');
  const [isReady, setIsReady] = React.useState(false);
  function onMouseOver() {
    setBgColor(__HOVER);
    setIsReady(true);
  }
  function onMouseLeave() {
    setBgColor(__BASE);
    setIsReady(false);
  }
  return (
    <div
      style={{
        backgroundColor: bgColor,
        cursor: 'default',
        // height: 36,
      }}
      className="d-flex"
      onMouseOver={onMouseOver}
      onMouseLeave={onMouseLeave}
    >
      <div
        style={{
          padding: '5px 10px',
          //   color: isSelecting ? 'white' : '#455A64',
        }}
        className="d-flex flex-1"
      >
        {`(${alarm.groupName})`} {alarm.name}
      </div>
      <div style={{ width: '18%' }}>
        {isReady ? (
          <Button
            classes="p-0 w-100 h-100"
            onClick={() => props.onClick(alarm)}
          >
            <i className="md-icon mr-0">add</i>
          </Button>
        ) : null}
      </div>
    </div>
  );
};

const UsingAlarmItem = ({ alarm, selected, ...props }) => {
  const [bgColor, setBgColor] = React.useState('white');
  const [isSelecting, setIsSelecting] = React.useState(false);
  const [isReady, setIsReady] = React.useState(false);
  React.useEffect(() => {
    setIsSelecting(isEqual(alarm, selected));
  }, [selected]);
  return (
    <div
      style={{
        backgroundColor: isSelecting ? __ACTIVE : bgColor,
        cursor: 'default',
      }}
      className="d-flex"
      onMouseOver={() => {
        setBgColor(__HOVER);
        setIsReady(true);
      }}
      onMouseLeave={() => {
        setBgColor(__BASE);
        setIsReady(false);
      }}
      onClick={() => props.onClick(isSelecting ? null : alarm)}
    >
      <div
        style={{
          padding: '5px 10px',
          color: isSelecting ? 'white' : '#455A64',
        }}
        className="d-flex flex-1"
      >
        {`(${alarm.groupName})`} {alarm.name}
      </div>
      <div style={{ width: '18%' }}>
        {isReady ? (
          <Button
            classes="p-0 w-100 h-100"
            onClick={() => props.onClickRemove(alarm)}
          >
            <i className="md-icon mr-0">remove</i>
          </Button>
        ) : null}
      </div>
    </div>
  );
};

export default Widget4Properties;
